/**
 * ReactSelect Component
 *
 * A customizable dropdown select component with support for both synchronous and asynchronous option loading.
 * Integrates with react-select and react-router-dom for context-aware API fetching.
 * Async options are loaded through `/proxy-api?operation=<apiOperationName>` with the user's Okta access token.
 *
 * @component
 * @example
 * const [error, setError] = useState({})
 * return (
 *   <ReactSelect
 *     name="searchField"
 *     asyncselect={true}
 *     apiOperationName="centralId"
 *     apiQuery="size=100"
 *     searchQueryKey="q"
 *     responseValueParser={(data) => data.results.map(item => ({ label: item.name, value: item.id }))}
 *     error={error.searchField}
 *     setError={setError}
 *   />
 * )
 *
 * @param {string} [name=''] - The field name, used for error handling
 * @param {boolean} [asyncselect=false] - If true, uses AsyncSelect for dynamic option loading; otherwise uses standard Select
 * @param {boolean} [error=false] - Error state flag to display error styling
 * @param {Function} [setError=() => {}] - Callback function to set errors in the form; receives (name, errorObject)
 * @param {Function} [responseValueParser=data => data.map(...)] - Function to parse API response into select options format
 * @param {string} [apiOperationName=''] - Proxy API operation name that the server maps to a downstream URL and method
 * @param {string} [searchQueryKey='value'] - Query parameter key for the search input
 * @param {boolean} [skipDefaultApiResponseHandling=false] - If true, bypasses default response validation and directly uses responseValueParser
 * @param {number} [debounceDelay=1000] - Debounce delay in milliseconds for API calls
 * @param {string} [apiQuery=''] - Extra query string (without a leading `?` or `&`) appended to the proxy request
 * @param {...*} restProps - Additional props passed to the Select/AsyncSelect component (e.g., isMulti, isClearable, onChange)
 * @returns {JSX.Element} A select dropdown component with optional async loading and error handling
 */

import { useEffect, useRef } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import Select from 'react-select'
import { useOutletContext } from 'react-router-dom'
import AsyncSelect from 'react-select/async'
import makeAnimated from 'react-select/animated'
import styles from './scss/ReactSelect.module.scss'

const animatedComponents = makeAnimated()

export default function ReactSelect({
  name = '',
  asyncselect = false,
  error = false,
  setError = () => {},
  responseValueParser = data => data.map(option => ({ label: option, value: option })),
  apiOperationName = '',
  searchQueryKey = 'value',
  skipDefaultApiResponseHandling = false,
  debounceDelay = 1000,
  apiQuery = '',
  ...restProps
}) {
  const debounceRef = useRef(null)

  const { lockAwareFetch } = useOutletContext()

  const { oktaAuth } = useOktaAuth()

  const loadOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(async () => {
      const {
        accessToken: { accessToken: token },
      } = await oktaAuth.tokenManager.getTokens()

      lockAwareFetch(
        `${window.env?.VITE_IAR_API}/proxy-api?operation=${apiOperationName}&${searchQueryKey}=${inputValue}&${apiQuery}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            authType: 'authblue',
          }),
        },
        60_000
      ).then(response => {
        if (skipDefaultApiResponseHandling) return callback(responseValueParser(response))

        if (response.message === 'Unable to process proxy API request') {
          setError(name, {
            type: 'manual',
            message: `Can't fetch options from server`,
          })
          return
        }

        if (response.content) {
          callback(responseValueParser(response.content))
        } else {
          callback([])
        }
      })
    }, debounceDelay)
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    []
  )

  const SelectComponent = asyncselect ? AsyncSelect : Select

  return (
    <SelectComponent
      className={`${styles.reactSelectWrapper} ${error ? styles.errorBorder : ''}`}
      placeholder='Type to search...'
      isMulti
      isClearable
      isSearchable
      components={animatedComponents}
      cacheOptions
      loadOptions={loadOptions}
      {...restProps}
    />
  )
}
